import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BrandIcon from '../components/BrandIcon';
import HreflangMeta from '../components/HreflangMeta';
import { handleTalkToSpecialist } from '../utils/webchat';
import './CategoryPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.libbenagents.com';

export default function CategoryPage() {
  const { category } = useParams();
  const { t, i18n } = useTranslation();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/public/agents`)
      .then((r) => r.json())
      .then((data) => {
        setAgents(data.agents || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const categoryName = t(`categories.${category}`, { defaultValue: category });
  const pageTitle = t('categoryPage.title', { category: categoryName });
  const metaDescription = t('categoryPage.metaDescription', { category: categoryName });

  // Categorias de sistema não são navegáveis no site público.
  const HIDDEN_CATEGORIES = ['interno'];
  const filtered = HIDDEN_CATEGORIES.includes(category)
    ? []
    : agents
        .filter((a) => a.category === category && a.enabled !== false)
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  return (
    <>
      <Helmet>
        <title>{pageTitle} | Libben Agents</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content="https://libbenagents.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <link rel="canonical" href={`https://libbenagents.com/agentes/categoria/${category}`} />
        <html lang={i18n.language} />
      </Helmet>
      <HreflangMeta />

      <div className="cat-page">
        <div className="cat-hero">
          <Link to="/agentes" className="cat-back">
            ← {t('marketplace.seeAll')}
          </Link>
          <h1>{pageTitle}</h1>
          <p>{metaDescription}</p>
        </div>

        {loading ? (
          <div className="cat-grid">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="mkt-card-skeleton" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="cat-empty">
            <p>{t('categoryPage.noAgents', { category: categoryName })}</p>
            <Link to="/agentes" className="cat-empty-link">
              {t('marketplace.seeAll')}
            </Link>
          </div>
        ) : (
          <div className="cat-grid">
            {filtered.map((agent) => (
              <div key={agent.agentId} className="mkt-card mkt-card-static">
                <div className="mkt-card-icon">
                  <BrandIcon icon={agent.icon || '🤖'} size={40} />
                </div>
                <div className="mkt-card-body">
                  <div className="mkt-card-cat">{categoryName}</div>
                  <div className="mkt-card-name">{agent.name}</div>
                  <div className="mkt-card-role">{agent.role}</div>
                  <p className="mkt-card-desc">{agent.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="cat-cta-bar">
          <div className="cat-cta-content">
            <h2>{t('categoryPage.ctaTitle')}</h2>
            <p>{t('categoryPage.ctaSubtitle')}</p>
          </div>
          <button
            type="button"
            onClick={handleTalkToSpecialist}
            className="cat-cta-btn"
          >
            {t('categoryPage.ctaButton')}
          </button>
        </div>
      </div>
    </>
  );
}
